import clsx from 'clsx'
import { format } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, NavLink } from 'react-router-dom'

import Avatar from '@/components-latest/primitives/Avatar'
import Button from '@/components-latest/primitives/Button'
import {
  ArrowLeft,
  SidebarAccount,
  SidebarBilling,
  SidebarMenu,
  SidebarOrder,
  SidebarPerformance
} from '@/components-latest/primitives/Icons'
import Timer from '@/components-latest/primitives/Icons/Timer'
import NotificationBell from '@/components-latest/primitives/NotificationBell'
import ScrollArea from '@/components-latest/primitives/Scrollable'
import SwitchWithWords from '@/components-latest/primitives/SwitchWithWords'
import SwitchWithWordsWithModal from '@/components-latest/primitives/SwitchWithWords/SwitchWithWordsWithModal'
import Text from '@/components-latest/primitives/Text'
import OnlineRestriction from '@/components-latest/ui/OnlineRestriction/OnlineRestriction'
import { PATHS } from '@/config/router/routes'
import { BUSY_STATUS, STORE_AVAILABILITY } from '@/constants/common'
import { selectIsSidebarOpen } from '@/ducks/app/selectors/app.selectors'
import { setSideBarOpen } from '@/ducks/app/slices/app.slice'
import { useGetOrdersCountByTypesQuery } from '@/ducks/orders/services/orders.api'
import {
  useChangeStoreAvailabilityMutation,
  useFetchStoreBranchesQuery,
  useGetStoreBusyStatusQuery
} from '@/ducks/store/api/store.api'
import {
  selectPhoneCode,
  selectStoreAvailability,
  selectStoreAvatar,
  selectStoreCashCoStatus,
  selectStoreId,
  selectStoreName,
  selectStoreType
} from '@/ducks/store/selectors/store.selectors'
import { setAutoBusy } from '@/ducks/store/slice/store.slice'
import { useDeviceSize } from '@/hooks/useDeviceSize/useDeviceSize'
import { logFirebaseEvent } from '@/utils/firebase.utils'

import Styles from './Sidebar.module.scss'

const Sidebar = () => {
  const { t } = useTranslation()

  const storeName = useSelector(selectStoreName)
  const storeId = useSelector(selectStoreId)
  const storeAvatar = useSelector(selectStoreAvatar)
  const storeAvailability = useSelector(selectStoreAvailability)
  const isStoreUsingCashCo = useSelector(selectStoreCashCoStatus)
  const storeType = useSelector(selectStoreType)
  const { data: branches } = useFetchStoreBranchesQuery()

  const {
    lmd_store_webapp_FR_Billing,
    lmd_store_webapp_offers_page,
    store_web_app_branches_management,
    store_web_app_busy_mode
  } = useFlags()
  const phoneCode = useSelector(selectPhoneCode)

  const { data: ordersCounts } = useGetOrdersCountByTypesQuery({
    store_id: storeId as string
  })

  const dispatch = useDispatch()
  const isSidebarOpen = useSelector(selectIsSidebarOpen)
  const onCloseSidebar = useCallback(() => {
    dispatch(setSideBarOpen(false))
  }, [dispatch])

  const { isMobile } = useDeviceSize(950)

  useEffect(() => {
    onCloseSidebar()
  }, [isMobile, onCloseSidebar])

  const [changeStoreAvailability, { isLoading }] =
    useChangeStoreAvailabilityMutation()

  const { data: busyStatusData, refetch: refetchBusyStatus } =
    useGetStoreBusyStatusQuery(undefined, {
      pollingInterval: 10000
    })
  const [isOnlineRestrictionOpen, setIsOnlineRestrictionOpen] = useState(false)

  useEffect(() => {
    dispatch(setAutoBusy(busyStatusData?.isPostRejection || false))
  }, [dispatch, busyStatusData?.isPostRejection])

  const onSwitchToggle = (newStatus?: 0 | 1 | 2) => {
    if (storeId && typeof storeAvailability === 'number') {
      if (newStatus === BUSY_STATUS.BUSY) {
        return
      }

      if (newStatus === STORE_AVAILABILITY.OFFLINE && storeAvailability === 0) {
        return
      }
      if (newStatus === STORE_AVAILABILITY.ONLINE && storeAvailability === 1) {
        return
      }
      if (newStatus === undefined) {
        return
      }

      if (newStatus === STORE_AVAILABILITY.OFFLINE) {
        changeStoreAvailability({
          availability: 0,
          store_id: storeId as string
        })
          .unwrap()
          .then(() => {
            logFirebaseEvent('AC_Web_Toggle_Off', {
              Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
              Store_ID: storeId
            })
          })
      } else if (newStatus === STORE_AVAILABILITY.ONLINE) {
        changeStoreAvailability({
          availability: 1,
          store_id: storeId as string
        })
          .unwrap()
          .then(() => {
            logFirebaseEvent('AC_Web_Toggle_On', {
              Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
              Store_ID: storeId
            })
          })
          .catch(error => {
            // Check if the error is OPENING_RESTRICTED
            if (error?.errorCode === 'OPENING_RESTRICTED') {
              setIsOnlineRestrictionOpen(false)
              setTimeout(() => {
                setIsOnlineRestrictionOpen(true)
              }, 0)
            }
          })
      }
    }
  }

  const pages = useMemo(
    () => [
      {
        url: PATHS.ORDERS,
        icon: <SidebarOrder />,
        name: t('common.page-names.orders'),
        notificationComponent: (
          <NotificationBell
            className={Styles.newOrderBell}
            count={ordersCounts?.new || 0}
          />
        ),
        hidden: false
      },
      {
        url: PATHS.MENU,
        icon: <SidebarMenu />,
        name: t('common.page-names.menu'),
        notificationComponent: null,
        hidden: false
      },
      {
        url: PATHS.OFFERS,
        icon: <SidebarMenu />,
        name: 'Offers',
        notificationComponent: null,
        hidden: !lmd_store_webapp_offers_page || storeType === 'dark_store'
      },
      {
        url: PATHS.PERFORMANCE,
        icon: <SidebarPerformance />,
        name: t('common.page-names.performance'),
        notificationComponent: null,
        hidden: false
      },
      {
        url: PATHS.BILLING,
        icon: <SidebarBilling />,
        name: 'Billing',
        notificationComponent: null,
        hidden:
          !isStoreUsingCashCo ||
          (!lmd_store_webapp_FR_Billing && phoneCode === '+33')
      },
      {
        url: PATHS.ACCOUNT,
        icon: <SidebarAccount />,
        name: t('common.page-names.account'),
        notificationComponent: null,
        hidden: false
      }
    ],
    [
      t,
      isStoreUsingCashCo,
      ordersCounts,
      lmd_store_webapp_offers_page,
      storeType
    ] // Add storeType to dependencies
  )

  return (
    <>
      <div
        className={clsx(Styles.sidebar, {
          [Styles.open]: isSidebarOpen
        })}>
        <ScrollArea>
          <div className={Styles.sidebarContent}>
            {isSidebarOpen && (
              <ArrowLeft
                width={31}
                height={31}
                onClick={onCloseSidebar}
                className={Styles.arrowLeft}
              />
            )}
            <Avatar
              size="large"
              string={storeName || ''}
              image={storeAvatar || ''}
            />
            <Text variant="nextTitle-3" weight="extraBold" asChild>
              <p>{storeName}</p>
            </Text>
            {store_web_app_busy_mode ? (
              <SwitchWithWordsWithModal
                onToggle={status => {
                  if (status !== undefined) {
                    onSwitchToggle(status)
                  }
                }}
                status={
                  // Always respect the actual store availability first, regardless of flag changes
                  storeAvailability === 0
                    ? STORE_AVAILABILITY.OFFLINE
                    : busyStatusData?.isBusy
                    ? BUSY_STATUS.BUSY
                    : BUSY_STATUS.NON_BUSY
                }
                isLoading={isLoading}
                className={Styles.switch}
                storeId={storeId as string}
              />
            ) : (
              <SwitchWithWords
                onToggle={(value?: boolean) => {
                  if (value !== undefined) {
                    const newStatus = value
                      ? STORE_AVAILABILITY.ONLINE
                      : STORE_AVAILABILITY.OFFLINE
                    onSwitchToggle(newStatus)
                  }
                }}
                checked={storeAvailability === 1}
                isLoading={isLoading}
                className={Styles.switch}
                checkedWord={t('switch-with-words.statuses.accepting')}
                uncheckedWord={t('switch-with-words.statuses.offline')}
                width={120}
              />
            )}
            {store_web_app_branches_management &&
              Array.isArray(branches) &&
              branches.length > 0 && (
                <Link
                  to="/branch/business-hours"
                  className={Styles.logoWrapper}>
                  <Button
                    variant="transparentOutlined"
                    icon={<Timer />}
                    size="large"
                    loading={isLoading}
                    shape="rounded"
                    className={Styles.smallBtn}>
                    Manage branch availability
                  </Button>
                </Link>
              )}
            <div className={Styles.links}>
              {pages.map(
                ({ name, url, icon, notificationComponent, hidden }, index) => {
                  if (hidden) return null
                  return (
                    <NavLink
                      key={index}
                      activeClassName={Styles.activeLink}
                      onClick={() => {
                        onCloseSidebar()
                      }}
                      className={Styles.link}
                      to={url}>
                      <>
                        {icon}
                        <span>{name}</span>
                        {notificationComponent}
                      </>
                    </NavLink>
                  )
                }
              )}
            </div>
          </div>
        </ScrollArea>
      </div>
      {isSidebarOpen && (
        <div
          data-testid="sidebar-overlay"
          onClick={onCloseSidebar}
          className={Styles.overlay}
        />
      )}
      {isOnlineRestrictionOpen && (
        <OnlineRestriction onClose={() => setIsOnlineRestrictionOpen(false)}>
          <div />
        </OnlineRestriction>
      )}
    </>
  )
}

export default Sidebar